import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Search, Bookmark, LogOut, UserRound } from "lucide-react";
import { Mark } from "@/components/Logo";
import { SearchCommand } from "@/components/SearchCommand";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { label: "RUN", to: "/run" },
  { label: "FIND A CLUB", to: "/clubs" },
  { label: "RANKED", to: "/ranked" },
  { label: "TRAINING", to: "/training" },
  { label: "DALLAS", to: "/dallas" },
  { label: "EVENTS", to: "/events" },
  { label: "STORIES", to: "/stories" },
  { label: "COLLABS", to: "/collabs" },
  { label: "SHOP", to: "/shop" },
  { label: "COACH", to: "/coach" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const location = useLocation();
  const { user, loading: authLoading, signIn, signOut } = useAuth();

  useEffect(() => {
    setOpen(false);
    setUserMenu(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((s) => !s);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-white/10 bg-ink/80 backdrop-blur-xl">
      <div className="flex items-center justify-between h-16 px-5 lg:px-10">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 text-white">
          <Mark white className="h-8 w-9" />
          <span className="font-display text-lg tracking-wide hidden sm:block">NINJA RUN CLUB</span>
          <span className="font-display text-lg tracking-wide sm:hidden">NRC</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `font-mono-label link-underline transition-colors duration-200 ${isActive ? "text-white" : "text-white/60 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-search-button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
            className="flex items-center gap-2 text-white/60 hover:text-white border border-white/20 hover:border-white/50 px-3 py-2 transition-colors duration-200"
          >
            <Search size={15} />
            <kbd className="font-mono-label hidden md:block text-white/40">⌘K</kbd>
          </button>
          <Link
            to="/join"
            data-testid="nav-join-cta"
            className="btn-invert hidden sm:inline-block bg-white text-ink font-mono-label px-5 py-2.5 hover:bg-ink hover:text-white border border-white"
          >
            JOIN THE CLUB
          </Link>
          {!authLoading && (user ? (
            <div className="relative hidden sm:block">
              <button
                data-testid="nav-user-menu-btn"
                onClick={() => setUserMenu((m) => !m)}
                className="flex items-center"
                aria-label="Account menu"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    referrerPolicy="no-referrer"
                    onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                    className="h-9 w-9 rounded-full border border-white/30 hover:border-white transition-colors duration-200"
                  />
                ) : null}
                <span style={{ display: user.picture ? "none" : "flex" }} className="h-9 w-9 rounded-full border border-white/30 items-center justify-center font-mono-label">{(user.name || "?")[0]}</span>
              </button>
              {userMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenu(false)} />
                  <div data-testid="nav-user-menu" className="absolute right-0 top-12 z-50 w-56 bg-night border border-white/15 shadow-2xl">
                    <div className="px-4 py-3 border-b border-white/10">
                      <p className="font-mono-label text-white truncate">{user.name?.toUpperCase()}</p>
                      <p className="text-xs text-white/40 truncate mt-0.5">{user.email}</p>
                    </div>
                    <Link to="/profile" data-testid="nav-profile" className="flex items-center gap-2.5 px-4 py-3 font-mono-label text-white/70 hover:bg-white hover:text-ink transition-colors duration-150">
                      <UserRound size={13} /> PROFILE
                    </Link>
                    <Link to="/my-clubs" data-testid="nav-my-clubs" className="flex items-center gap-2.5 px-4 py-3 font-mono-label text-white/70 hover:bg-white hover:text-ink transition-colors duration-150">
                      <Bookmark size={13} /> MY CLUBS
                    </Link>
                    <button
                      data-testid="nav-sign-out"
                      onClick={() => { setUserMenu(false); signOut(); }}
                      className="w-full flex items-center gap-2.5 px-4 py-3 font-mono-label text-white/70 hover:bg-white hover:text-ink transition-colors duration-150 border-t border-white/10"
                    >
                      <LogOut size={13} /> SIGN OUT
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <button
              data-testid="nav-sign-in"
              onClick={signIn}
              className="hidden sm:inline-block font-mono-label border border-white/30 px-4 py-2.5 text-white/70 hover:border-white hover:text-white transition-colors duration-200"
            >
              SIGN IN
            </button>
          ))}
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            data-lenis-prevent
            data-testid="nav-mobile-panel"
            className="lg:hidden fixed inset-x-0 top-16 h-[calc(100dvh-4rem)] overflow-y-auto border-t border-white/10 bg-ink"
          >
            <div className="flex flex-col px-5 py-4">
              {LINKS.map((l, i) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${i}`}
                  className="font-display text-2xl py-3 text-white/80 hover:text-white border-b border-white/5"
                >
                  {l.label}
                </Link>
              ))}
              <Link to="/join" data-testid="nav-mobile-join" className="mt-4 bg-white text-ink font-mono-label px-5 py-3.5 text-center">
                JOIN THE CLUB
              </Link>
              {!authLoading && (user ? (
                <div className="flex gap-3 mt-3">
                  <Link to="/profile" data-testid="nav-mobile-profile" className="flex-1 border border-white/25 text-center font-mono-label px-4 py-3 text-white/80">
                    PROFILE
                  </Link>
                  <Link to="/my-clubs" data-testid="nav-mobile-my-clubs" className="flex-1 border border-white/25 text-center font-mono-label px-4 py-3 text-white/80">
                    MY CLUBS
                  </Link>
                </div>
              ) : (
                <button onClick={signIn} data-testid="nav-mobile-sign-in" className="mt-3 border border-white/25 font-mono-label px-4 py-3 text-white/80">
                  SIGN IN WITH GOOGLE
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      <SearchCommand open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};
